import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { FadeUp, SlideLeft, SlideRight } from '../../../components/shared/Motion';
import { EditableText, EditableImage, HrefPill } from '../../components';

function AdmissionsSteps({ data, onChange }) {
  const update = (patch) => onChange && onChange({ ...data, ...patch });
  const items = data.items || [];
  const updateItem = (i, patch) => {
    const next = items.map((x, idx) => (idx === i ? { ...x, ...patch } : x));
    update({ items: next });
  };
  return (
    <section id="admissions" className="relative py-24 lg:py-32 bg-white overflow-hidden">
      <div className="container-page">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <SlideLeft className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-44 h-44 opacity-50 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(161,201,164,0.45) 1.5px, transparent 1.5px)', backgroundSize: '18px 18px' }} />
              <div className="relative overflow-hidden rounded-3xl shadow-[0_30px_60px_-25px_rgba(46,58,24,0.5)]">
                <EditableImage
                  src={data.image?.src}
                  alt={data.image?.alt || 'Campus'}
                  onChange={(src, alt) => update({ image: { src, alt } })}
                  className="h-[520px] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-olive-footer/40 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-white text-xs font-semibold uppercase tracking-[0.18em]">
                    <Sparkles size={12} className="text-accent" />
                    <EditableText value={data.image_badge} onChange={(t) => update({ image_badge: t })} as="span" />
                  </div>
                </div>
              </div>
            </div>
          </SlideLeft>

          <SlideRight className="lg:col-span-7">
            <span className="eyebrow"><span className="h-px w-8 bg-olive" />
              <EditableText value={data.eyebrow} onChange={(t) => update({ eyebrow: t })} as="span" />
            </span>
            <h2 className="section-title mt-4">
              <EditableText value={data.heading_prefix} onChange={(t) => update({ heading_prefix: t })} as="span" />
              <span className="text-gradient"><EditableText value={data.heading_highlight} onChange={(t) => update({ heading_highlight: t })} as="span" /></span>
              {data.heading_suffix && <EditableText value={data.heading_suffix} onChange={(t) => update({ heading_suffix: t })} as="span" />}
            </h2>
            <p className="section-lead">
              <EditableText value={data.subheading} onChange={(t) => update({ subheading: t })} as="span" multiline />
            </p>

            <div className="mt-10 space-y-5">
              {items.map((s, i) => (
                <FadeUp key={i} delay={i * 0.1}>
                  <div className="group flex items-start gap-5 p-5 rounded-2xl border border-olive/10 hover:border-accent/40 hover:bg-olive-light/40 transition-all duration-300">
                    <div className="relative shrink-0">
                      <div className="absolute inset-0 rounded-full bg-accent/40 blur-md opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="relative inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent text-ink font-display font-extrabold text-lg shadow-[0_10px_24px_-8px_rgba(197,120,99,0.7)]">
                        <EditableText value={s.number} onChange={(t) => updateItem(i, { number: t })} as="span" />
                      </div>
                    </div>
                    <div>
                      <EditableText value={s.title} onChange={(t) => updateItem(i, { title: t })} as="h4" className="font-display text-lg font-bold text-olive-deep" />
                      <EditableText value={s.desc} onChange={(t) => updateItem(i, { desc: t })} as="p" multiline className="mt-1 text-ink/70 leading-relaxed" />
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>

            {data.cta && (
              <div className="mt-10 inline-flex flex-col">
                <Link to={data.cta.href || '#'} className="group relative inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-olive text-white font-semibold text-sm hover:bg-olive-deep hover:-translate-y-0.5 hover:shadow-glow-olive transition-all duration-300">
                  <EditableText value={data.cta.label} onChange={(t) => update({ cta: { ...(data.cta || {}), label: t } })} as="span" />
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <HrefPill value={data.cta.href} onChange={(h) => update({ cta: { ...(data.cta || {}), href: h } })} />
              </div>
            )}
          </SlideRight>
        </div>
      </div>
    </section>
  );
}

export default AdmissionsSteps;
